import { z } from "zod";
import { utcDateTimeWithOptionalSecondsSchema } from "./primitives.js";
const hash = z.string().regex(/^[a-f0-9]{64}$/u);
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/u);
const absolutePath = z.string().startsWith("/");
/** Public selection contains no paths, endpoints, prompts or tool authority. */
export const frozenEvaluationRequestSchema = z.object({
    manifestHash: hash,
    caseId: id,
    armId: id,
    attemptId: id,
}).strict();
export const frozenEvaluationArmSchema = z.object({
    provider: z.enum(["openai", "google", "modal"]),
    model: z.string().trim().min(1).max(200),
    reasoning: z.enum(["off", "minimal", "low", "medium", "high", "xhigh", "max"]).optional(),
    transport: z.enum(["codex_cli", "google", "openai_completions"]),
    endpointBaseUrl: z.string().url().optional(),
    apiKeyEnv: z.string().regex(/^[A-Z][A-Z0-9_]+$/u).optional(),
}).strict().superRefine((arm, context) => {
    const expected = { openai: "codex_cli", google: "google", modal: "openai_completions" };
    if (arm.transport !== expected[arm.provider]) {
        context.addIssue({ code: "custom", message: "Provider transport must match; OpenAI requires the official Codex CLI." });
    }
    if (arm.provider === "openai" && (arm.endpointBaseUrl || arm.apiKeyEnv)) {
        context.addIssue({ code: "custom", message: "Codex subscription arms cannot specify an API endpoint or API key." });
    }
    if (arm.provider === "modal") {
        const url = arm.endpointBaseUrl && URL.canParse(arm.endpointBaseUrl) ? new URL(arm.endpointBaseUrl) : undefined;
        if (!url || url.protocol !== "https:" || !url.hostname.endsWith(".modal.run") ||
            url.username || url.password || url.search || url.hash || !/^\/v1\/?$/u.test(url.pathname) || !arm.apiKeyEnv) {
            context.addIssue({ code: "custom", message: "Modal arms require an authenticated exact HTTPS modal.run /v1 endpoint." });
        }
    }
});
export const frozenEvaluationBudgetsSchema = z.object({
    maxOutputTokens: z.number().int().min(1).max(131_072),
    maxToolCalls: z.number().int().min(0).max(256),
    timeoutMs: z.number().int().min(1_000).max(3_600_000),
}).strict();
export const frozenEvaluationCaseSchema = z.object({
    prompt: z.string().min(1).max(100_000),
    context: z.string().max(200_000).optional(),
    domain: id,
    toolPolicy: z.enum(["none", "evidence", "sandbox"]),
    maxToolCalls: z.number().int().min(0).max(256).optional(),
}).strict();
export const frozenEvaluationManifestSchema = z.object({
    protocol: z.literal("mlx.frozen-evaluation.v1"),
    tenantSlug: z.string().min(1),
    organisationId: z.string().min(1),
    // Hand-written in manifests and stored in bindings; see utcDateTimeWithOptionalSecondsSchema.
    clock: utcDateTimeWithOptionalSecondsSchema,
    evidenceRoot: absolutePath,
    evidenceManifestSha256: hash,
    caseInstructions: z.string().max(100_000).optional(),
    budgets: frozenEvaluationBudgetsSchema,
    arms: z.record(id, frozenEvaluationArmSchema),
    cases: z.record(id, frozenEvaluationCaseSchema),
    explorer: frozenEvaluationArmSchema.optional(),
}).strict();
export const frozenEvaluationChildSchema = z.object({
    parentRunId: z.string().min(1), parentSessionId: z.string().min(1),
    parentWorkerId: z.string().min(1), parentAttemptNumber: z.number().int().positive(),
    assignmentId: id, assignment: z.string().trim().min(1).max(10_000),
    maxToolCalls: z.number().int().min(1).max(4),
    deadlineAt: z.number().int().positive(),
}).strict();
export const FROZEN_EVALUATION_EXPLORER_PROMPT = "Gather evidence only for the parent's bounded assignment. The parent owns interpretation, assumptions, decisions and the final answer. Report ambiguity, sources, observations, failures and uncertainty. Use only case-bound read-only evidence tools. Do not write sandbox state, expand scope or address the user.";
/** Selected server-owned authority only; other cases never travel to a worker. */
export const frozenEvaluationBindingSchema = frozenEvaluationRequestSchema.extend({
    protocol: z.literal("mlx.frozen-evaluation.v1"),
    tenantSlug: z.string().min(1),
    organisationId: z.string().min(1),
    // Hand-written in manifests and stored in bindings; see utcDateTimeWithOptionalSecondsSchema.
    clock: utcDateTimeWithOptionalSecondsSchema,
    evidenceRoot: absolutePath,
    evidenceManifestSha256: hash,
    caseInstructions: z.string().max(100_000).optional(),
    budgets: frozenEvaluationBudgetsSchema,
    arm: frozenEvaluationArmSchema,
    case: frozenEvaluationCaseSchema,
    explorer: frozenEvaluationArmSchema.optional(),
    child: frozenEvaluationChildSchema.optional(),
}).strict();
export function frozenEvaluationChildBinding(parent, child) {
    if (!parent.explorer || parent.child || parent.case.toolPolicy === "none")
        throw new Error("Frozen evaluation explorer is unavailable for this parent.");
    return frozenEvaluationBindingSchema.parse({ ...parent, child, arm: parent.explorer,
        budgets: { maxOutputTokens: Math.min(parent.budgets.maxOutputTokens, 4096),
            maxToolCalls: Math.min(parent.budgets.maxToolCalls, child.maxToolCalls), timeoutMs: Math.min(parent.budgets.timeoutMs, 120_000) } });
}
export function frozenEvaluationLane(manifestHash) {
    return `evaluation:${hash.parse(manifestHash)}`;
}
